import React, { useEffect, useRef, useCallback } from 'react';
import withStyles from '@mui/styles/withStyles';
import { useStylesCustomTable } from './Style';
import createToast from 'components/Toast/Toast';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Button,
  Grid,
} from '@mui/material';
import { InputV2, AutocompleteV, Icons , InputCurrency} from 'components';
import { useProductsList } from 'hooks';
import _ from 'lodash';
import useCustomHook from '../../hooks';

export default function CustomizedTables({
  methods,
  defaultValues,
  selectedProducts,
  setSelectedProducts,
}) {
  const classes = useStylesCustomTable();
  const {
    productSearchText,
    setProductSearchText,
    newProductName,
    setNewProductName,
    currentProduct,
    setCurrentProduct,
  } = useCustomHook();

  const { productsList, getProductsList, productsLoading } = useProductsList();
  const delayedQuery = useRef(_.debounce((v, f) => f(v), 500)).current;

  useEffect(() => {
    delayedQuery(productSearchText, getProductsList);
  }, [productSearchText]);

  useEffect(() => {
    methods.register('product');
    return () => {
      methods.unregister('product');
    };
  }, [methods.register, methods.unregister]);

  const productsTableData = () => {
    const formValues = methods.getValues();
    const data = selectedProducts.map?.((p, i) => ({
      deleteAction: (
        <IconButton onClick={() => removeProduct(i)} size="large">
          <Icons icon="Trash" color="#2b3990" size="20" />
        </IconButton>
      ),
      id: p.id,
      name: p.name,
      description: (
        <InputV2
          name={`product[${i}].description`}
          label="توضیحات"
          showLabel={false}
          errors={methods.formState.errors}
          register={methods.register}
          control={methods.control}
          style={{ marginTop: 16 }}
        />
      ),
      price: (
        <InputCurrency
          name={`product[${i}].price`}
          label="قیمت"
          showLabel={false}
          errors={methods.formState.errors}
          register={methods.register}
          rules={['required', 'isInt', 'isMin|0']}
          control={methods.control}
          minimumValue="1"
          currency
          defaultValue="1"
          style={{ marginTop: 16 }}
          onChangeValue={(price) => {
            const discount = Number(formValues.product?.[i]?.discount);
            if (Number(discount) > price) {
              methods.setError(`product[${i}].discount`, {
                message: `تخفیف نباید از ${price} بیشتر باشد`,
              });
            } else if (Number(discount) <= price) {
              methods.setError(`product[${i}].discount`, {
                message: null,
              });
            }
          }}
        />
      ),
      quantity: (
        <InputV2
          name={`product[${i}].quantity`}
          label="تعداد"
          showLabel={false}
          errors={methods.formState.errors}
          register={methods.register}
          rules={['required', 'isInt', 'isMin|1']}
          control={methods.control}
          minimumValue="1"
          currency
          currencyText="عدد"
          defaultValue="1"
          style={{ marginTop: 16 }}
        />
      ),
      discount: (
        <InputCurrency
          name={`product[${i}].discount`}
          label="تخفیف"
          showLabel={false}
          errors={methods.formState.errors}
          register={methods.register}
          rules={[
            'isInt',
            'isMin|0',
            'required',
            `isMax|${methods.watch(`product[${i}].price`) ?? 1}`,
          ]}
          control={methods.control}
          minimumValue="0"
          defaultValue="0"
          style={{ marginTop: 16 }}
        />
      ),
    }));

    const removeProduct = (i) => {
      const newState = selectedProducts.filter((_, index) => index !== i);
      setSelectedProducts(newState);
    };

    return data || [];
  };

  const onAddItem = useCallback(
    (p) => {
      if (!p.name) {
        return createToast('خطا', 'عنوان محصول را وارد کنید!', 'danger');
      }
      setProductSearchText('');
      setSelectedProducts((state) => [
        ...state,
        {
          id: p.id,
          name: p.name,
          description: '',
          price: '1',
          quantity: 1,
          discount: 0,
        },
      ]);
      setCurrentProduct(null);
    },
    [currentProduct],
  );

  return (
    <TableContainer component={Paper}>
      <Table className={classes.table} aria-label="customized table">
        <TableHead>
          <TableRow>
            <StyledTableCell align="center">ردیف</StyledTableCell>
            <StyledTableCell align="center">شناسه محصول</StyledTableCell>
            <StyledTableCell align="center">نام محصول</StyledTableCell>
            <StyledTableCell align="center" width="30%">
              توضیحات
            </StyledTableCell>
            <StyledTableCell align="center">قیمت</StyledTableCell>
            <StyledTableCell align="center">تعداد</StyledTableCell>
            <StyledTableCell align="center">تخفیف</StyledTableCell>
            <StyledTableCell align="center">عملیات</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {productsTableData().map?.((p, i) => (
            <StyledTableRow key={i}>
              {[
                i + 1,
                p.id,
                p.name,
                p.description,
                p.price,
                p.quantity,
                p.discount,
                p.deleteAction,
              ].map((x) => (
                <StyledTableCell>{x}</StyledTableCell>
              ))}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
      <TableFooter>
        <Grid alignItems="center" className={classes.addBusinessWrapper}>
          <div className={classes.autocompleteWrapper}>
            <AutocompleteV
              placeholder="جستجو در محصولات کسب‌وکار..."
              control={methods.control}
              filterOptions={(options) => options}
              errors={methods.formState.errors}
              register={methods.register}
              name="product_auto"
              list={productsList}
              preventAddCustomOption
              style={{ marginBottom: -4 }}
              loading={productsLoading}
              getOptionSelected={(option, value) => option.id === value.id}
              getOptionDisabled={(option) =>
                selectedProducts.find((v) => v?.id === option?.id) != null
              }
              inputValue={productSearchText ?? ''}
              onInputChange={(e) => {
                if (e?.target?.value === productSearchText) return;
                setProductSearchText(e?.target?.value ?? '');
              }}
              disableClearable
              defaultValue={
                defaultValues?.id
                  ? {
                    value: defaultValues?.id,
                    name: defaultValues?.name,
                    label: `${defaultValues?.name} - ${defaultValues?.id}`,
                  }
                  : null
              }
              onChangeValue={(p) => {
                if (!p) return;
                onAddItem(p);
              }}
            />
          </div>
          <Typography className={classes.orText}>یا</Typography>
          <InputV2
            name="newProductName"
            errors={{}}
            forceValue={newProductName}
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                e.preventDefault();
                onAddItem({ id: 0, name: newProductName });
                setNewProductName('');
                return false;
              }
            }}
            onChangeValue={(e) => {
              setNewProductName(e?.target?.value ?? '');
            }}
            defaultValue=""
            removeLeftRadius
            placeholder="عنوان محصول جدید..."
            style={{
              marginBottom: -4,
              minWidth: 250,
            }}
          />
          <Button
            onClick={() => {
              onAddItem({ id: 0, name: newProductName });
              setNewProductName('');
            }}
            variant="outlined"
            color="primary"
            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
          >
            افزودن
          </Button>
          { }
        </Grid>
      </TableFooter>
    </TableContainer>
  );
}

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: 'rgb(238, 241, 245)',
    color: '#546e7a',
  },
  body: {
    fontSize: 14,
    paddingTop: 0,
    paddingBottom: 0,
  },
}))(TableCell);

const StyledTableRow = withStyles(() => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#f9f9f9',
    },
  },
}))(TableRow);
